import { apiRequest } from './core/client';
import {
  EmailSchema,
  type SearchResult,
  type SearchHighlight,
} from './schemas';

export type { SearchResult };

// ============================================================================
// Unified Search Types
// ============================================================================

export interface CalendarEventSearchResult {
  kind: 'calendar_event';
  accountId: string;
  calendar_event: {
    id: string;
    accountId: string;
    googleEventId: string;
    calendarUid: string;
    summary: string | null;
    description: string | null;
    location: string | null;
    startDate: string | null;
    startDateTime: string | null;
    startTimezone: string | null;
    endDate: string | null;
    endDateTime: string | null;
    endTimezone: string | null;
    allDay: boolean;
    organizer: { email?: string; displayName?: string } | null;
    attendees: Array<{ email?: string; displayName?: string; responseStatus?: string }> | null;
    status: string;
    htmlLink: string | null;
    conferenceData: unknown | null;
    similarity?: number;
  };
}

export interface TaskSearchResult {
  kind: 'task';
  task: {
    id: string;
    title: string;
    notesMarkdown?: string;
    status: string;
    priority?: string;
    dueDate?: string;
    projectId?: string;
    similarity?: number;
    ftsRank?: number;
  };
  _score?: number;
}

export type UnifiedSearchResultItem =
  | (SearchResult & { _score?: number; threadCount?: number; matchedIn?: string[] })
  | TaskSearchResult
  | (CalendarEventSearchResult & { _score?: number });

export interface UnifiedSearchResponse {
  /** Ranked email results for the requested page (one row per thread) */
  results: UnifiedSearchResultItem[];
  /** Capped tasks / calendar events that also matched (first page only) */
  related: UnifiedSearchResultItem[];
  hasMore: boolean;
}

// ============================================================================
// Unified Search
// ============================================================================

function mapResultItem(item: unknown, accountId: string): UnifiedSearchResultItem {
  const record = item as Record<string, unknown>;
  const kind = record.kind as string;

  if (kind === 'email') {
    const emailData = record.email ?? record;
    const parsed = EmailSchema.safeParse(emailData);
    if (parsed.success) {
      return {
        kind: 'email' as const,
        accountId:
          typeof record.accountId === 'string'
            ? record.accountId
            : typeof record.account_id === 'string'
              ? record.account_id
              : accountId,
        email: parsed.data,
        highlight: record.highlight as SearchHighlight[] | undefined,
        _score: record._score as number | undefined,
        threadCount: typeof record.threadCount === 'number' ? record.threadCount : 1,
        matchedIn: Array.isArray(record.matchedIn) ? (record.matchedIn as string[]) : undefined,
      };
    }
  }

  if (kind === 'task') {
    return item as TaskSearchResult;
  }

  if (kind === 'calendar_event') {
    const eventItem = item as CalendarEventSearchResult & { account_id?: string; _score?: number };
    return {
      ...eventItem,
      accountId: eventItem.account_id || accountId,
    };
  }

  return item as UnifiedSearchResultItem;
}

export async function unifiedSearch(
  accountId: string,
  query: string,
  options: {
    limit?: number;
    offset?: number;
    types?: string[];
    quick?: boolean;
    /** Fold Blocked-folder mail back into results (excluded by default) */
    includeBlocked?: boolean;
  } = {}
): Promise<UnifiedSearchResponse> {
  const data = await apiRequest<{ results?: unknown[]; related?: unknown[]; hasMore?: boolean }>({
    path: '/search/unified',
    accountId,
    query: {
      q: query,
      limit: options.limit,
      offset: options.offset,
      types: options.types?.join(','),
      // Type-ahead mode: keyword list only, skips the sender-diverse and
      // semantic passes for speed
      quick: options.quick ? '1' : undefined,
      include_blocked: options.includeBlocked ? '1' : undefined,
    },
  });

  const results = Array.isArray(data?.results) ? data.results : [];
  const related = Array.isArray(data?.related) ? data.related : [];
  return {
    results: results.map((item) => mapResultItem(item, accountId)),
    related: related.map((item) => mapResultItem(item, accountId)),
    hasMore: Boolean(data?.hasMore),
  };
}
